import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface PCInvoiceItem {
  id: string;
  referrer_name: string;
  referrer_type: string;
  invoice_number: string;
  patient_name: string;
  service_date: string;
  service_amount: number;
  commission_rate: number;
  commission_amount: number;
  payment_status: string;
  payment_date?: string;
}

export default function PCInvoiceReport() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<PCInvoiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [referrerTypeFilter, setReferrerTypeFilter] = useState<string>('all');

  useEffect(() => {
    loadPCInvoiceReport();
  }, [startDate, endDate, statusFilter, referrerTypeFilter]);

  const loadPCInvoiceReport = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockPCInvoiceReport());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('referral_accruals')
        .select(`
          id,
          referrer_name,
          service_date,
          service_amount,
          fee_amount,
          status,
          payment_date,
          invoice_id,
          patient_id,
          contract:referral_contracts(referrer_type, commission_rate),
          invoice:invoices(invoice_number),
          patient:patients(name)
        `);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      if (referrerTypeFilter !== 'all') {
        query = query.eq('contract.referrer_type', referrerTypeFilter);
      }
      if (startDate) {
        query = query.gte('service_date', startDate);
      }
      if (endDate) {
        query = query.lte('service_date', endDate);
      }

      const { data, error } = await query.order('service_date', { ascending: false });
      if (error) throw error;

      const formattedData: PCInvoiceItem[] = (data || []).map(item => ({
        id: item.id,
        referrer_name: item.referrer_name,
        referrer_type: item.contract?.referrer_type || 'N/A',
        invoice_number: item.invoice?.invoice_number || 'N/A',
        patient_name: item.patient?.name || 'N/A',
        service_date: item.service_date,
        service_amount: item.service_amount,
        commission_rate: item.contract?.commission_rate || 0,
        commission_amount: item.fee_amount,
        payment_status: item.status,
        payment_date: item.payment_date,
      }));

      setReportData(formattedData);
    } catch (error) {
      console.error('Error loading PC invoice report:', error);
      setReportData(getMockPCInvoiceReport());
    } finally {
      setLoading(false);
    }
  };

  const getMockPCInvoiceReport = (): PCInvoiceItem[] => {
    return [
      {
        id: '1',
        referrer_name: 'Dr. Ahmed Rahman',
        referrer_type: 'doctor',
        invoice_number: 'INV-2025-001',
        patient_name: 'John Doe',
        service_date: '2025-01-27',
        service_amount: 5000,
        commission_rate: 10,
        commission_amount: 500,
        payment_status: 'paid',
        payment_date: '2025-01-28',
      },
      {
        id: '2',
        referrer_name: 'City Diagnostic Center',
        referrer_type: 'organization',
        invoice_number: 'INV-2025-002',
        patient_name: 'Jane Smith',
        service_date: '2025-01-26',
        service_amount: 3000,
        commission_rate: 15,
        commission_amount: 450,
        payment_status: 'pending',
      },
      {
        id: '3',
        referrer_name: 'Dr. Fatima Khan',
        referrer_type: 'doctor',
        invoice_number: 'INV-2025-003',
        patient_name: 'Ahmed Khan',
        service_date: '2025-01-25',
        service_amount: 8000,
        commission_rate: 12,
        commission_amount: 960,
        payment_status: 'paid',
        payment_date: '2025-01-27',
      },
    ];
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.referrer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.patient_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalCommission = filteredData.reduce((sum, item) => sum + item.commission_amount, 0);
  const totalPaid = filteredData.filter(item => item.payment_status === 'paid').reduce((sum, item) => sum + item.commission_amount, 0);
  const totalPending = filteredData.filter(item => item.payment_status === 'pending').reduce((sum, item) => sum + item.commission_amount, 0);

  const handleExportCSV = () => {
    const headers = ['Referrer Name', 'Type', 'Invoice #', 'Patient', 'Service Date', 'Service Amount', 'Commission Rate', 'Commission Amount', 'Status', 'Payment Date'];
    const rows = filteredData.map(item => [
      item.referrer_name,
      item.referrer_type,
      item.invoice_number,
      item.patient_name,
      item.service_date,
      item.service_amount,
      `${item.commission_rate}%`,
      item.commission_amount,
      item.payment_status,
      item.payment_date || 'N/A',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pc-invoice-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">P.C Invoice Report</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Commission</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(totalCommission)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Paid Commission</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalPaid)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Pending Commission</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{formatCurrency(totalPending)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search referrer, invoice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <select
                value={referrerTypeFilter}
                onChange={(e) => setReferrerTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="doctor">Doctor</option>
                <option value="organization">Organization</option>
                <option value="agent">Agent</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referrer Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      No PC invoices found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.referrer_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {item.referrer_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.invoice_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.patient_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.service_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.service_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.commission_rate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-bold">
                        {formatCurrency(item.commission_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            item.payment_status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {item.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
